import { motion } from "framer-motion";
import { Navigation } from "lucide-react";
import { ATTRACTIONS } from "@/data/content";

const spanClass = {
  wide: "sm:col-span-2 aspect-[16/8]",
  tall: "sm:row-span-2 aspect-[4/3] sm:aspect-auto",
  std: "aspect-[4/3]",
};

const reveal = {
  hidden: { opacity: 0, y: 36 },
  show: { opacity: 1, y: 0, transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] } },
};

export default function Attractions() {
  return (
    <section id="explore" className="bg-forest-dark px-4 py-20 sm:px-8 lg:px-16 lg:py-28">
      <div className="mx-auto max-w-7xl">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold-light">
              Nearest Attractions · <span className="font-kannada normal-case tracking-normal">ಸುತ್ತಮುತ್ತಲಿನ ಸ್ಥಳಗಳು</span>
            </p>
            <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-amber-50 sm:text-4xl lg:text-4xl">
              Temples, cliffs <span className="italic text-gold-gradient">& the open sea</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-amber-100/70">
            Anil Palace sits at the perfect midpoint — sacred shrines to the east, golden
            coastline to the west. Our front desk plans day trips and temple taxis.
          </p>
        </motion.div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {ATTRACTIONS.map((a, i) => (
            <motion.article
              key={a.id}
              data-testid={a.testid}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.05 }}
              className={`group relative overflow-hidden rounded-lg gold-frame ${spanClass[a.span]}`}
            >
              <img
                src={a.image}
                alt={a.name}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forest-dark/95 via-forest-dark/35 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
                <div className="mb-3 flex items-center gap-3">
                  <span className="rounded-full bg-gold px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-forest-dark">
                    {a.distance}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-amber-100/80">
                    <Navigation className="h-3 w-3" /> {a.time}
                  </span>
                </div>
                <h3 className="font-display text-xl font-semibold text-amber-50 sm:text-2xl">
                  {a.name}
                </h3>
                <p className="font-kannada text-xs text-gold-light">{a.kannada}</p>
                <p className="mt-2 max-w-md text-xs leading-relaxed text-amber-100/75 sm:text-sm">
                  {a.text}
                </p>
              </div>
            </motion.article>
          ))}

          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            className="flex aspect-[4/3] flex-col justify-between rounded-lg border border-gold/30 bg-maroon p-6"
          >
            <p className="font-display text-xl italic leading-snug text-amber-100 sm:text-2xl">
              "Coming from Kerala? We are your first halt after the border — and your
              gateway to Mookambika."
            </p>
            <p className="mt-4 text-xs leading-relaxed text-amber-100/70">
              കേരളത്തിൽ നിന്ന് വരുന്ന തീർത്ഥാടകർക്ക് പ്രത്യേക സ്വാഗതം — മൂകാംബിക ദർശനത്തിന്
              ഏറ്റവും അനുയോജ്യമായ താമസം.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
